import React, { FC, memo, useEffect, useMemo, useState } from 'react'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { Marquee } from '@/components/Marquee'
import { eventStatus } from '@/constants/common'
import { icons } from '@/constants/icons'
import { BodyText } from '@/containers/styles'
import { useRedeemablePPVEvent } from '@/hooks'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { ApiService } from '@/service'
import useStore from '@/store/useStore'
import { SportCategoryType } from '@/types/servicesTypes/sportCategoriesTypes'
import { getBadges } from '@/utils/eventUtils'

import {
  BodyTextWrapper,
  EventHeadingWrapper,
  LabelIcon,
  LockIcon,
  NonWatchableLabel,
  PremiumLabel,
  PurchasedLabel,
  StyledTitle,
  TimeLabel,
} from './styles'

const EventHeadingRaw: FC<{
  currentEventStatus: Nullable<eventStatus>
}> = (props) => {
  const { currentEventStatus } = props
  const { t } = useCommonTranslation()
  const { isRegisteredUser } = useAuth()

  const [category, setCategory] = useState<Nullable<SportCategoryType>>(null)
  const event = useStore((state) => state.common.currentEvent)

  const isRedeemablePPVEvent = useRedeemablePPVEvent(event)
  const isWatchableEvent = Boolean(event?.watchable)

  const { isAccessible, isPremium, isPPVEvent } = useMemo(() => {
    return getContentAccessFlags(event, null, isRegisteredUser)
  }, [event, isRegisteredUser])

  const {
    isLiveNow,
    isTodayEvent,
    timeLabel,
    isBlockedPremiumChannel,
    isBlockedPPVEvent,
    isBlockedPromotionEvent,
    isSubscribedPremiumChannel,
    isPurchasedPPVEvent,
  } = useMemo(
    () =>
      getBadges(isAccessible, isPPVEvent, isPremium, isWatchableEvent, event),
    [
      event,
      isAccessible,
      isPPVEvent,
      isPremium,
      isWatchableEvent,
      currentEventStatus,
    ],
  )

  const showLockIcon =
    !!event &&
    isPPVEvent &&
    !isAccessible &&
    isWatchableEvent &&
    isRedeemablePPVEvent === false

  useEffect(() => {
    const controller = new AbortController()
    const abortSignal = controller.signal

    if (!event?.categoryId) {
      setCategory(null)
    }
    if (event?.categoryId && category?.id !== event.categoryId) {
      ApiService.getSportCategoryById(event.categoryId, abortSignal)
        .then((res) => {
          if (!controller.signal.aborted) {
            setCategory(res)
          }
        })
        .catch(() => {
          setCategory(null)
        })
    }

    return () => {
      controller.abort()
    }
  }, [event])

  const name = event?.name
  const eventUrl = `http://${window.location.host}/event/${event?.id}`

  return (
    <EventHeadingWrapper>
      <StyledTitle>
        <Marquee autoplay speed={10} delay={1.5} withPause>
          {isDevMode ? <a href={eventUrl}>{name}</a> : name}
        </Marquee>
      </StyledTitle>

      <BodyTextWrapper>
        {!!timeLabel && (
          <TimeLabel $isLiveNow={isLiveNow} $isToday={isTodayEvent}>
            {timeLabel}
          </TimeLabel>
        )}
        {isBlockedPPVEvent && (
          <PremiumLabel>
            <LabelIcon src={icons.navigation.blockedIcon} />
            {isBlockedPromotionEvent
              ? t('ppvPopup.promotion')
              : t('ppvPopup.payPerView')}
          </PremiumLabel>
        )}
        {isBlockedPremiumChannel && (
          <PremiumLabel>
            <LabelIcon src={icons.navigation.blockedIcon} />
            {t('premiumPass')}
          </PremiumLabel>
        )}
        {(isSubscribedPremiumChannel || isPurchasedPPVEvent) && (
          <PurchasedLabel>
            <LabelIcon src={icons.navigation.subscribedIcon} />
            {isSubscribedPremiumChannel ? t('subscribed') : t('bought')}
          </PurchasedLabel>
        )}
        {!isWatchableEvent && (
          <NonWatchableLabel>
            <LabelIcon src={icons.navigation.infoIcon} />
            {t('programInfoOnly')}
          </NonWatchableLabel>
        )}
        <BodyText>{category?.name}</BodyText>
        {showLockIcon && <LockIcon src={icons.navigation.lockIcon} alt="" />}
      </BodyTextWrapper>
    </EventHeadingWrapper>
  )
}

export const EventHeading = memo(EventHeadingRaw)
